//@flow
import { ArrowDownwardOutlined, ArrowRightOutlined } from "@mui/icons-material";
import { Collapse, ListItem, ListItemButton, Paper } from "@mui/material";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import InfoIcon from "../../assets/images/icons/sideMenu/infoIcon";
import ContentLoader from "../../components/general/contentLoader/contentLoader";
import { useDragDropTree } from "../../components/general/dragDropTree/useDragDropTree";
import { Permissions } from "../../helpers/constants";
import { useFAQAction } from "../../hooks/faqs/useFAQAction";
import { useFAQData } from "../../hooks/faqs/useFAQData";
import { EXHIBITOR_PROFILE } from "../../locales/components/namespaces";
import { useHasPermission } from "../../modules/permission/hooks/useHasPermission";
import ViewRow from "./partials/viewRow";

type Props = {
	exhibitionId: string
}

const FaqsView = (props: Props) => {
	const { exhibitionId } = props
	const theme = useTheme()
	const { t } = useTranslation(EXHIBITOR_PROFILE)
	const { getFaqs } = useFAQAction()
	const { faqs, loading } = useFAQData({ exhibitionId: exhibitionId })
	const isOrganizer = useHasPermission([Permissions.BeOrganizer], { shouldView: true })
	useEffect(() => {
		getFaqs(exhibitionId)
	}, [exhibitionId, getFaqs])
	const { itemsList } = useDragDropTree({ unprocessedItems: faqs })
	const urlParams = document.location.search
	const faqUrlIsOpen = urlParams.includes('faqId')
	const [open, setOpen] = useState(!!faqUrlIsOpen)
	return (
		<Box>
			<List dense sx={{ width: '100%', pt: 0 }}>
				<Paper
					sx={{
						background: faqUrlIsOpen && !isOrganizer ? theme.palette.highlight.dark : theme.palette.highlight.main,
						...(faqUrlIsOpen && !isOrganizer ? { boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset' } : {}),
						borderTopRightRadius: '12px',
						borderTopLeftRadius: '12px',
						borderBottomLeftRadius: open ? 0 : '12px',
						borderBottomRightRadius: open ? 0 : '12px',
						...(!isOrganizer ? {
							'&:hover': {
								background: theme.palette.highlight.dark,
								boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
							}
						} : {})
					}}
				>
					<ListItemButton
						disableGutters
						onClick={(event) => {
							event.stopPropagation()
							if (!isOrganizer) setOpen(prev => !prev)
						}}
						sx={{
							p: '16px',
							marginBottom: open ? 0 :'8px',
							background: faqUrlIsOpen && !isOrganizer ? theme.palette.highlight.dark : theme.palette.highlight.main,
							...(faqUrlIsOpen && !isOrganizer ? { boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset' } : {}),
							borderTopRightRadius: '12px',
							borderTopLeftRadius: '12px',
							borderBottomLeftRadius: open ? 0 : '12px',
							borderBottomRightRadius: open ? 0 : '12px',
							...(!isOrganizer ? {
								'&:hover': {
									background: theme.palette.highlight.dark,
									boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
								}
							} : {})
						}}
					>
						<ListItemIcon sx={{ minWidth: '24px', mr: 0.5 }}>
							<InfoIcon/>
						</ListItemIcon>
						<ListItemText
							secondary={<Typography
								sx={{
									width: '100%',
									color: '#fff',
									overflowWrap: 'break-word',
									wordBreak: 'break-word',   /* fallback */
									whiteSpace: 'normal'
								}}
								variant={'button'}
								component={'div'}
							>
								{t('Setup – Operation – Dismantling')}<br/>
							</Typography>
							}
						>
							<Typography
								sx={{
									width: '100%',
									color: '#fff'
								}}
								variant={'button'}
								component={'div'}
							>
								{t('General Info')}<br/>
							</Typography>
						</ListItemText>
						{!isOrganizer && (
							<>
								{open ? <ArrowRightOutlined style={{ color: '#FFFFFF' }}/> :
									<ArrowDownwardOutlined style={{ fontSize: 18, color: '#FFFFFF' }}/>}
							</>
						)}
					</ListItemButton>
				</Paper>
				<Collapse in={isOrganizer ? true : open} timeout="auto" unmountOnExit>
					{itemsList && itemsList.length > 0 ? (
						<Paper
							elevation={0}
							sx={{
								background: '#ECF4FF',
								borderBottomLeftRadius: '12px',
								borderBottomRightRadius: '12px',
								borderTopLeftRadius: 0,
								borderTopRightRadius: 0,
								p: '0px 16px 0px 16px',
							}}
						>
							{itemsList.map((f => (
								<ViewRow
									key={f.id}
									item={f.data}
									depth={f.depth}
									exhibitionId={exhibitionId}
								/>
							)))}
						</Paper>
					) : (
						<ContentLoader
							loading={loading}
						/>
					)}
				</Collapse>
			</List>
		</Box>
	)
}

export default FaqsView;
